import { Component, Input, OnInit } from '@angular/core';
import { ImportContainer } from "../../../../../Model/dataProcess/importContainer";
import { WawiListService } from "../../../../../Services/wawiList.service";
import { LoginService } from "../../../../../Services/login.service";
import { TranslateService } from "@ngx-translate/core";
import { SystemService } from "../../../../../Services/system.service";
import { lastValueFrom } from "rxjs";
import { confirm } from "devextreme/ui/dialog";
import { ImportContainerService } from "../../../../../Services/DataProcess/import-container.service";
import { FIELD_LIST, GROUP_LIST } from "../vaildUnitMappingFields";
import { MappingType, WawiList } from "../../../../../Model/wawi/WawiList";

type FieldTreeItem = {
    parentId: string;
    field: string;
    translatedField?: string;
};

type MappingStatus = {
    icon: string;
    cssClass: string;
    text: string;
};

@Component({
    selector: 'np-others-mapping-tab',
    templateUrl: './np-others-mapping-tab.component.html',
    styleUrl: './np-others-mapping-tab.component.css',
    standalone: false
})
export class NpOthersMappingTabComponent implements OnInit {
    @Input() container: ImportContainer;

    private readonly MAX_SELECTED = 7;

    mappings: Array<WawiList> = [];
    allMappings: Array<WawiList> = [];
    mappingInEdit: WawiList;
    fieldList: { field: string, translatedField: string }[];
    fieldListTree: FieldTreeItem[] = [];

    addMappingPopupVisible: boolean = false;
    createMappingPopupVisible: boolean = false;
    editMappingPopupVisible: boolean = false;

    selectedGlobalMappingId: string = null;
    globalMappingOptions: { id: string, label: string }[] = [];
    newMapping: WawiList;
    newMappingIsContainerSpecific: boolean = false;

    mappingTypeOptions: { id: number, name: string }[];
    nullValueOptions: { id: boolean, name: string }[];
    mappingScopeOptions: { id: boolean, name: string }[];

    readonly MappingType = MappingType;

    constructor(
        public wawiListService: WawiListService,
        public loginService: LoginService,
        public translate: TranslateService,
        public systemService: SystemService,
        public importContainerService: ImportContainerService
    ) {
        this.openAddMappingPopup = this.openAddMappingPopup.bind(this);
        this.closeAddMappingPopup = this.closeAddMappingPopup.bind(this);
        this.addExistingMapping = this.addExistingMapping.bind(this);
        this.openCreateMappingPopup = this.openCreateMappingPopup.bind(this);
        this.closeCreateMappingPopup = this.closeCreateMappingPopup.bind(this);
        this.createMapping = this.createMapping.bind(this);
        this.duplicateMapping = this.duplicateMapping.bind(this);
        this.deleteMapping = this.deleteMapping.bind(this);
        this.openEditMappingPopup = this.openEditMappingPopup.bind(this);
        this.closeEditMappingPopup = this.closeEditMappingPopup.bind(this);
        this.saveMappingEdit = this.saveMappingEdit.bind(this);

        this.mappingTypeOptions = [
            { id: MappingType.Manual, name: this.translate.instant('manuell') },
            { id: MappingType.FieldSpecific, name: this.translate.instant('Feldspezifisch') }
        ];
        this.nullValueOptions = [
            { id: false, name: this.translate.instant('Nicht anlegen') },
            { id: true, name: this.translate.instant('anlegen') }
        ];
        this.mappingScopeOptions = [
            { id: false, name: this.translate.instant('Global') },
            { id: true, name: this.translate.instant('Container spezifisch') }
        ];

        this.fieldList = FIELD_LIST.map(item => ({
            field: item.field.toString(),
            translatedField: this.translate.instant(item.field.toString())
        }));

        this.buildFieldTree();
    }

    async ngOnInit() {
        await this.loadMappings();
    }

    private buildFieldTree() {
        this.fieldListTree = GROUP_LIST.map(item => ({
            parentId: null,
            field: item.toString(),
            translatedField: this.translate.instant(item.toString())
        } as FieldTreeItem));

        FIELD_LIST.forEach(item => {
            let group = this.fieldListTree.find(x => x.field == item.group.toString());
            if (group != null) {
                this.fieldListTree.push({
                    parentId: group.field,
                    field: item.field.toString(),
                    translatedField: this.translate.instant(item.field.toString())
                } as FieldTreeItem);
            }
        });
    }

    private async loadMappings() {
        if (!this.container?.id)
            return;

        this.allMappings = await this.wawiListService.getAllMappings(this.loginService.currentCustomer.id);
        this.mappings = this.allMappings.filter(mapping => this.container.otherMappingsToExecute.includes(mapping.id));
        this.mappings.forEach(mapping => mapping.execute = true);
    }

    private buildGlobalMappingOptions() {
        this.globalMappingOptions = this.allMappings
            .filter(mapping => !mapping.sourceValuesCatalogId
                && !this.container.otherMappingsToExecute.includes(mapping.id))
            .map(mapping => ({ id: mapping.id, label: `${mapping.listName} (${this.mappingTypeLabel(mapping.mappingType)})` }));
    }

    mappingTypeLabel(mappingType: MappingType): string {
        return mappingType == MappingType.FieldSpecific
            ? this.translate.instant('Feldspezifisches Mapping')
            : this.translate.instant('Manuelles Mapping');
    }

    openAddMappingPopup() {
        this.selectedGlobalMappingId = null;
        this.buildGlobalMappingOptions();
        this.addMappingPopupVisible = true;
    }

    closeAddMappingPopup() {
        this.addMappingPopupVisible = false;
    }

    async addExistingMapping() {
        if (!this.selectedGlobalMappingId) {
            this.systemService.notifyInfo(this.translate.instant("Bitte ein Mapping auswählen!"));
            return;
        }
        this.addMappingPopupVisible = false;

        let mapping = this.allMappings.find(x => x.id == this.selectedGlobalMappingId);
        await this.assignToContainer(mapping.id);
        mapping.execute = true;
        this.mappings.push(mapping);
    }

    openCreateMappingPopup() {
        this.addMappingPopupVisible = false;
        this.newMapping = new WawiList("", "", "", "", true, [], true, MappingType.Manual, false, []);
        this.newMappingIsContainerSpecific = false;
        this.createMappingPopupVisible = true;
    }

    closeCreateMappingPopup() {
        this.createMappingPopupVisible = false;
    }

    mappingTypeChanged(e) {
        this.newMapping.mappingType = e.value;
        if (this.newMapping.mappingType == MappingType.Manual) {
            this.newMapping.createNullValues = false;
        }
    }

    get selectedChildCount(): number {
        return this.newMapping.mappingFields.filter(field => this.isFieldChild(field)).length;
    }

    private isFieldChild(field: string): boolean {
        let item = this.fieldListTree.find(x => x.field == field);
        return item != null && item.parentId != null;
    }

    isSelected(item: FieldTreeItem): boolean {
        return this.newMapping.mappingFields.includes(item.field.toString());
    }

    isLeafDisabled(item: FieldTreeItem): boolean {
        if (item.parentId == null)
            return true;
        if (this.isSelected(item))
            return false;
        return this.selectedChildCount >= this.MAX_SELECTED;
    }

    onFieldSelectionChanged(item: FieldTreeItem, e: any) {
        if (!e?.event)
            return;
        if (this.isSelected(item)) {
            this.newMapping.mappingFields = this.newMapping.mappingFields.filter(field => field != item.field.toString());
        } else {
            this.newMapping.mappingFields.push(item.field.toString());
        }
    }

    async createMapping() {
        if (!this.newMapping.listName) {
            this.systemService.notifyInfo(this.translate.instant("Bitte eine Namen angeben!"));
            return;
        }
        if (this.newMapping.mappingType == MappingType.Manual && this.newMapping.mappingFields.length == 0) {
            this.systemService.notifyInfo(this.translate.instant("Bitte mindestens ein Zielfeld auswählen!"));
            return;
        }
        this.createMappingPopupVisible = false;

        this.newMapping.sourceValuesCatalogId = this.newMappingIsContainerSpecific
            ? this.container.activeCatalogId
            : null;

        let createdMapping = await this.wawiListService.create(this.newMapping, this.loginService.currentCustomer.id);
        createdMapping.execute = true;

        this.allMappings.push(createdMapping);
        await this.assignToContainer(createdMapping.id);
        this.mappings.push(createdMapping);
    }

    private async assignToContainer(mappingId: string) {
        if (!this.container.otherMappingsToExecute.includes(mappingId)) {
            this.container.otherMappingsToExecute.push(mappingId);
            await this.importContainerService.update(this.container, this.loginService.currentCustomer.id);
        }
    }

    private async unassignFromContainer(mappingId: string) {
        if (this.container.otherMappingsToExecute.includes(mappingId)) {
            this.container.otherMappingsToExecute = this.container.otherMappingsToExecute.filter(x => x != mappingId);
            await this.importContainerService.update(this.container, this.loginService.currentCustomer.id);
        }
    }

    mappingValidityLabel(mapping: WawiList): string {
        return !mapping.sourceValuesCatalogId
            ? this.translate.instant('Global')
            : this.translate.instant('Container spezifisch');
    }

    getMappingStatus(mapping: WawiList): MappingStatus {
        const fields = mapping.fields ?? [];
        const hasSource = fields.some(f => (f.key ?? '').toString().trim().length > 0);
        const hasTarget = fields.some(f => (f.value ?? '').toString().trim().length > 0);

        if (mapping.mappingType == MappingType.FieldSpecific
            && this.container?.activeCatalogId
            && mapping.sourceValuesCatalogId
            && mapping.sourceValuesCatalogId != this.container.activeCatalogId) {
            return {
                icon: 'warning',
                cssClass: 'mapping-status--new-catalog',
                text: this.translate.instant('Neuer Katalog – Quellwerte prüfen')
            };
        }

        if (!hasSource && !hasTarget) {
            return {
                icon: 'warning',
                cssClass: 'mapping-status--no-values',
                text: this.translate.instant('Keine Werte')
            };
        }

        if (fields.some(f => (f.value ?? '').toString().trim().length == 0)) {
            return {
                icon: 'warning',
                cssClass: 'mapping-status--unmapped',
                text: this.translate.instant('Nicht alle Werte zugewiesen')
            };
        }

        return {
            icon: 'check',
            cssClass: 'mapping-status--complete',
            text: this.translate.instant('Vollständig')
        };
    }

    async updateMapping(e) {
        let mapping = e.data;
        if (mapping.execute) {
            await this.assignToContainer(mapping.id);
        } else {
            await this.unassignFromContainer(mapping.id);
        }
    }

    openEditMappingPopup(e) {
        this.mappingInEdit = structuredClone(e.row.data);
        this.editMappingPopupVisible = true;
    }

    closeEditMappingPopup() {
        this.editMappingPopupVisible = false;
    }

    private hasUnassignedValues(mapping: WawiList): boolean {
        return (mapping.fields ?? []).some(field =>
            (field.key ?? '').toString().trim().length > 0
            && (field.value ?? '').toString().trim().length == 0);
    }

    async saveMappingEdit() {
        if (this.hasUnassignedValues(this.mappingInEdit)) {
            let message = this.translate.instant('Nicht alle Zielwerte wurden zugewiesen. Leere Zielwerte überschreiben beim Anwenden den bestehenden Produktwert mit leer. Trotzdem speichern?');
            let title = this.translate.instant('Mapping speichern');
            let proceed = await confirm(message, title);
            if (!proceed)
                return;
        }

        this.editMappingPopupVisible = false;

        let mapping = this.mappings.find(x => x.id == this.mappingInEdit.id);
        mapping.mappingFields = this.mappingInEdit.mappingFields;
        mapping.fields = this.mappingInEdit.fields;
        mapping.mappingType = this.mappingInEdit.mappingType;
        mapping.createNullValues = this.mappingInEdit.createNullValues;

        await this.wawiListService.update(mapping, this.loginService.currentCustomer.id);
    }

    async duplicateMapping(e) {
        let source = e.row.data as WawiList;
        let copy = new WawiList("", `${source.listName} (Kopie)`, source.key, source.value, true,
            structuredClone(source.fields), true, source.mappingType,
            source.createNullValues, [...source.mappingFields]);

        let createdMapping = await this.wawiListService.create(copy, this.loginService.currentCustomer.id);
        createdMapping.execute = true;
        this.allMappings.push(createdMapping);
        await this.assignToContainer(createdMapping.id);
        this.mappings.push(createdMapping);
    }

    async deleteMapping(e) {
        let message = this.translate.instant('Wollen Sie das Mapping wirklich löschen');
        let title = this.translate.instant('Mapping Löschen');
        let result = await confirm(message, title);
        if (!result)
            return;

        await lastValueFrom(this.wawiListService.delete(e.row.data.id, this.loginService.currentCustomer.id));
        await this.unassignFromContainer(e.row.data.id);

        this.mappings = this.mappings.filter(x => x.id != e.row.data.id);
        this.allMappings = this.allMappings.filter(x => x.id != e.row.data.id);
    }
}
